import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  SxProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import React, { ReactElement, useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';

import { legacyColors } from '~/legacyTheme';

import { LegacyFAIcon, LegacyFAIcons } from '../LegacyFAIcon';
import { LegacyMuiButton } from '../LegacyForms';

import { useSxStyles } from './useStyles';

export type Props = {
  open: boolean;
  showSave: boolean;
  showCancel: boolean;
  sxStylesDialogFooter?: SxProps<Theme>;
  showCustomButton?: boolean;
  hideTopClose?: boolean;
  customIcon?: string;
  customText?: string;
  customCancelText?: string;
  title: string;
  icon?: string;
  sxStylesDialogPaper?: SxProps<Theme>;
  sxStylesDialogContent?: SxProps<Theme>;
  sxStylesDialogTitleToolbar?: SxProps<Theme>;
  sxStylesDialogTitleText?: SxProps<Theme>;
  maxWidth?: DialogProps['maxWidth'];
  additionalHeaderInfo?: ReactElement;
  formId?: string;
  onClose: (event?: object, reason?: string) => void;
  onSave?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  backDropInvisible?: boolean;
  variant?: 'standard' | 'warning' | 'error';
  submitting?: boolean;
};

export const LegacyDialog: React.FC<Props> = ({
  children,
  open,
  showSave,
  showCancel,
  showCustomButton,
  customIcon,
  customText,
  customCancelText,
  hideTopClose,
  title,
  icon,
  sxStylesDialogFooter = undefined,
  sxStylesDialogPaper = undefined,
  sxStylesDialogContent = undefined,
  sxStylesDialogTitleToolbar = undefined,
  sxStylesDialogTitleText = undefined,
  maxWidth = 'sm',
  additionalHeaderInfo,
  formId,
  onClose,
  onSave,
  backDropInvisible = false,
  variant = 'standard',
  submitting = false,
}) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const [dialogId, setDialogId] = useState('');
  const showSubmitButton = showSave || showCustomButton;
  const submitBtnIcon = customIcon || 'fa fa-floppy-o';
  const submitBtnText = customText || t('save');
  const cancelBtnText = customCancelText || t('cancel');

  useEffect(() => {
    setDialogId(uniqueId('draggable-dialog-'));
  }, [setDialogId]);
  return (
    <Backdrop invisible={backDropInvisible} sx={sxStyles.backDrop} open={open}>
      <Draggable handle={`#${dialogId}`}>
        <Dialog
          sx={sxStyles.dialog}
          hideBackdrop={true}
          open={open}
          onClose={onClose}
          maxWidth={maxWidth}
          PaperProps={{ sx: sxStylesDialogPaper }}
        >
          <DialogTitle
            sx={{
              ...(sxStylesDialogTitleToolbar || {}),
              ...(sxStyles.titleDiv || {}),
              ...(variant ? sxStyles[variant] || {} : {}),
            }}
          >
            <Box id={dialogId} sx={sxStyles.title}>
              <div>
                {icon && <i className={classNames(icon, sxStyles.titleIcon)} />}
                <Box
                  component="span"
                  sx={{
                    ...(sxStylesDialogTitleText || {}),
                    ...(sxStyles.titleText || {}),
                  }}
                >
                  {t(title)}
                </Box>
                {additionalHeaderInfo}
              </div>
              {!hideTopClose ? (
                <IconButton
                  size="small"
                  onClick={onClose}
                  className="fa fa-times"
                  sx={{
                    ...{
                      color:
                        variant === 'standard'
                          ? legacyColors.blue
                          : legacyColors.white,
                    },
                    ...(sxStyles.dialogClose || {}),
                  }}
                />
              ) : undefined}
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              ...(sxStylesDialogContent || {}),
              ...(sxStyles.dialogContent || {}),
            }}
          >
            {children}
          </DialogContent>
          <DialogActions
            sx={{
              ...(sxStylesDialogFooter || {}),
              ...(sxStyles.dialogActions || {}),
            }}
          >
            {showSubmitButton && (
              <Box sx={sxStyles.footerDialog}>
                <LegacyMuiButton
                  form={formId}
                  type={formId ? 'submit' : 'button'}
                  onClick={formId ? undefined : onSave}
                  disabled={submitting}
                  startIcon={
                    submitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <LegacyFAIcon FAIcon={submitBtnIcon} />
                    )
                  }
                >
                  {submitBtnText}
                </LegacyMuiButton>
              </Box>
            )}
            {showCancel && (
              <Box sx={sxStyles.footerDialog}>
                <LegacyMuiButton
                  onClick={onClose}
                  startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Close} />}
                >
                  {cancelBtnText}
                </LegacyMuiButton>
              </Box>
            )}
          </DialogActions>
        </Dialog>
      </Draggable>
    </Backdrop>
  );
};
